import { useCallback } from 'react';
import {
  useMotionValue,
  useTransform,
  animate,
  type PanInfo,
} from 'motion/react';
import { FlashcardCorrection } from '../../typologies/flashcard/useFlashcardAnswer';
import {
  SWIPE_THRESHOLD,
  SWIPE_OFFSCREEN,
  SWIPE_MAX_ROTATION,
  flashcardSwipeTransition,
  flashcardSwipeOutTransition,
} from '@/utils/animations/flashcardAnimation';

type UseFlashcardSwipeProps = {
  onSwipe: (value: FlashcardCorrection) => void;
  enabled: boolean;
};

export function useFlashcardSwipe({
  onSwipe,
  enabled,
}: UseFlashcardSwipeProps) {
  const x = useMotionValue(0);
  const opacity = useMotionValue(1);
  const rotate = useTransform(
    x,
    [-SWIPE_OFFSCREEN, SWIPE_OFFSCREEN],
    [-SWIPE_MAX_ROTATION, SWIPE_MAX_ROTATION],
  );

  const successOpacity = useTransform(x, [0, SWIPE_THRESHOLD], [0, 1]);
  const errorOpacity = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0]);

  const triggerSwipe = useCallback(
    (value: FlashcardCorrection) => {
      const direction = value === 'true' ? 1 : -1;
      opacity.set(1);
      animate(x, direction * SWIPE_OFFSCREEN, flashcardSwipeOutTransition);
      animate(opacity, 0, flashcardSwipeOutTransition);
      onSwipe(value);
    },
    [x, opacity, onSwipe],
  );

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (!enabled) return;

    const offset = info.offset.x;

    if (Math.abs(offset) < SWIPE_THRESHOLD) {
      animate(x, 0, flashcardSwipeTransition);
      return;
    }

    triggerSwipe(offset > 0 ? 'true' : 'false');
  };

  return {
    x,
    opacity,
    rotate,
    successOpacity,
    errorOpacity,
    handleDragEnd,
    triggerSwipe,
  };
}

export type UseFlashcardSwipeReturn = ReturnType<typeof useFlashcardSwipe>;
